import { useMutation } from '@tanstack/react-query';
import { t } from '@lingui/core/macro';
import { BookmarkActionViewMode } from '../types/api';
import { User } from '../types/api';
import { useToast } from '../hooks/useToast';

export type DownloadFormat = 'kindle' | 'epub' | 'printable' | 'rss';
export type DownloadSectionType = BookmarkActionViewMode;

/** File formats generated on the server and emailed to the user. */
export type FileDownloadFormat = 'epub' | 'pdf';

/** Sections that support generating an ePub/PDF export. */
export function canExportFiles(mode: string): boolean {
  return ['home', 'unread', 'folder', 'tags'].includes(mode);
}

interface FileDownloadParams {
  format: FileDownloadFormat;
  folderId?: string;
  tagSlug?: string;
}

interface FileDownloadResponse {
  success?: boolean;
  message?: string;
}

/**
 * Request server-side generation of an ePub/PDF export. The backend enqueues a
 * task and emails the file, so this resolves once the request is accepted; it
 * does not download a file directly. Returns the localized status message the
 * backend produced so it can be surfaced in a toast.
 */
export async function requestFileDownload({
  format,
  folderId,
  tagSlug,
}: FileDownloadParams): Promise<FileDownloadResponse> {
  const params = new URLSearchParams();
  if (folderId) {
    params.set('folder_id', folderId);
  } else if (tagSlug) {
    params.set('tag', tagSlug);
  }

  const response = await fetch(`/user/download/${format}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
    body: params.toString(),
  });

  if (!response.ok) {
    throw new Error(`Download request failed with status ${response.status}`);
  }

  return response.json();
}

/**
 * Mutation for requesting an ePub/PDF export. Shows a success toast with the
 * backend message, or an error toast on failure.
 */
export function useRequestFileDownload() {
  const { showToast } = useToast();

  return useMutation({
    mutationFn: requestFileDownload,
    onSuccess: (data) => {
      // The backend response carries the already-localized message (from the
      // Tornado translation catalog), so it is the single source of truth for
      // this copy; we intentionally don't duplicate it as a frontend string.
      if (data.message) {
        showToast(data.message, 'success');
      }
    },
    onError: () => {
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
  });
}

export function generateRSSDownloadURL(
  mode: string,
  currentUser?: User,
  folderId?: string,
  tagSlug?: string,
): string | null {
  if (!currentUser) {
    return null;
  }
  if (['home', 'unread'].includes(mode)) {
    return `/rss/${currentUser.id}/${currentUser.rssKey}`;
  }
  if (['liked', 'archive', 'videos'].includes(mode)) {
    return `/${mode}/rss/${currentUser.id}/${currentUser.rssKey}`;
  } else if (mode == 'folder' && folderId) {
    return `/folder/${folderId}/rss/${currentUser.id}/${currentUser.rssKey}`;
  } else if (mode == 'tags' && tagSlug) {
    return `/tags/${tagSlug}/rss/${currentUser.id}/${currentUser.rssKey}`;
  }
  return null;
}

/**
 * Check if user has Kindle configured
 * This would typically be part of user settings/preferences
 */
export async function hasKindleConfigured(): Promise<boolean> {
  try {
    // TODO: Replace with actual API endpoint when available
    // For now, assume Kindle is configured if user is logged in
    return true;
  } catch {
    return false;
  }
}
